// AB#2412 — enum values are stored as SCREAMING_SNAKE codes (ACTIVE, RUN_OUT, MOVING_AVERAGE) and were
// rendered raw, so the UI shouted status values at the user. This converts a code to a human label for
// DISPLAY ONLY.
//
// The stored code is never touched. Codes are referenced by processes, validate rules, readiness rules,
// the wasm subscribers, view showIf expressions and the list archive config — rewriting stored values
// would break all of those and need a data migration. This is the same code-stored / label-shown split
// that lookupValue fields already have.

/**
 * Converts a stored enum code to a display label.
 *
 * `RUN_OUT` → `Run Out`, `MOVING_AVERAGE` → `Moving Average`, `ACTIVE` → `Active`.
 *
 * Two deliberate exclusions:
 * - A value that already contains a lowercase letter is returned untouched, so an entity that authored
 *   `Active` or a free-text value is never re-cased.
 * - A token containing a digit keeps its original casing (`UK01` stays `UK01`, not `Uk01`), because
 *   those read as codes rather than words.
 */
export function formatEnumLabel(code: string): string {
    if (!code) { return code }
    // Anything with lowercase in it is already human-authored — leave it alone.
    if (/[a-z]/.test(code)) { return code }
    return code
        .split('_')
        .filter(t => t.length > 0)
        .map(t => (/\d/.test(t) ? t : t.charAt(0) + t.slice(1).toLowerCase()))
        .join(' ')
}

/**
 * AB#2913 — one enum option as authored: a bare code, or a `{ value, label }` pair for a code whose
 * displayed text cannot be derived from it.
 *
 * The pair exists because a module does not always choose its own stored values. A document line's
 * status is written by `derive-document-status` from the server's own constants, so a purchasing line
 * stores `PARTIALLY_DELIVERED` whatever purchasing calls it — and humanising that gives "Partially
 * Delivered" on a line that was in fact partly ORDERED.
 *
 * This replaces the `CODE:Label` colon encoding, which could not carry a label containing a colon and
 * collided with the cascading-dropdown `{parentValue}:{option}` prefix.
 */
export type EnumOptionSource = string | { value: string; label?: string | null; tone?: string | null }

/** A resolved option: the stored value, and the text to show for it. */
export interface EnumOption {
    value: string
    label: string
}

/**
 * Normalises authored options into `{ value, label }`, resolving the label exactly as the server's
 * `EnumOption.DisplayLabel` does: a declared label wins, otherwise the code is humanised.
 *
 * Every consumer goes through here. Three separate places used to parse the colon form inline — the
 * form select, the header status select and the metaOptions status list — and the filter bar did not
 * humanise at all, so the same value read differently depending on which control you looked at.
 */
export function enumOptions(entries: readonly EnumOptionSource[] | null | undefined): EnumOption[] {
    if (!entries) { return [] }
    return entries.map(e => (typeof e === 'string'
        ? { value: e, label: formatEnumLabel(e) }
        : { value: e.value, label: e.label ? e.label : formatEnumLabel(e.value) }))
}

/** Retains authored/dynamic labels when an editable enum becomes a read-only field. */
export function enumOptionLabel(entries: readonly EnumOptionSource[] | null | undefined, value: unknown): string | undefined {
    if (value === null || value === undefined) return undefined
    return enumOptions(entries).find(option => option.value === String(value))?.label
}
